import { useMutation, useQueryClient } from 'react-query';
import { debounce } from 'lodash';
import { useCallback, useRef, useEffect } from 'react';
import { enqueueSnackbar } from 'notistack';
import dayjs from 'dayjs';
import { performanceReportingApiUrl, fetcher, ServerErrorResponse } from 'data_sources/constants';
import { Template } from '../_types';

// Minimal helper function to prepare template for API - only removes problematic nested objects
export const cleanTemplateForApi = (template: Template): any => {
  // Remove parentTemplate nested object (only send parentTemplateId and parentVersionId)
  const { parentTemplate, ...rest } = template;

  return {
    ...rest,
    templateSections: template.templateSections.map((section) => {
      // Remove nested parentTemplateSection object
      const { parentTemplateSection, ...sectionRest } = section;

      return {
        ...sectionRest,
        sectionFields: section.sectionFields.map((field) => {
          // Remove nested objects that break API
          const { fieldCategory, parentSectionField, ...fieldRest } = field;

          // Normalize date/time values for API consistency (defaultValues is string[])
          let defaultValues = fieldRest.defaultValues;
          if (field.fieldType === 'Date' && defaultValues && Array.isArray(defaultValues) && defaultValues.length > 0) {
            defaultValues = defaultValues.map((v) => {
              const date = dayjs(v);
              return date.isValid() ? date.startOf('day').utc().toISOString() : v;
            });
          } else if (field.fieldType === 'Time' && defaultValues && Array.isArray(defaultValues) && defaultValues.length > 0) {
            defaultValues = defaultValues.map((v) => {
              const time = dayjs(v);
              return time.isValid() ? time.toISOString() : v;
            });
          }

          return {
            ...fieldRest,
            defaultValues,
          };
        }),
      };
    }),
  };
};

// Minimal helper function to transform API response - recursively process parentTemplate
export const transformApiResponseToFrontend = (template: Template): Template => {
  // Recursively transform parentTemplate if it exists
  const transformedParentTemplate = template.parentTemplate
    ? transformApiResponseToFrontend(template.parentTemplate)
    : null;

  return {
    ...template,
    parentTemplate: transformedParentTemplate,
    templateSections: template.templateSections.map((section) => ({
      ...section,
      sectionFields: section.sectionFields.map((field) => {
        // Normalize date values from API to frontend format (defaultValues/effectiveDefaultValues are string[])
        // Defensive: ensure arrays (API may have returned string in older contract)
        let defaultValues = Array.isArray(field.defaultValues)
          ? field.defaultValues
          : field.defaultValues != null
            ? [String(field.defaultValues)]
            : null;
        let effectiveDefaultValues = Array.isArray(field.effectiveDefaultValues)
          ? field.effectiveDefaultValues
          : field.effectiveDefaultValues != null && field.effectiveDefaultValues !== ''
            ? [String(field.effectiveDefaultValues)]
            : [];

        if (field.fieldType === 'Date' && defaultValues && Array.isArray(defaultValues)) {
          defaultValues = defaultValues.map((v) => {
            const date = dayjs.utc(v).local();
            return date.isValid() ? date.format('YYYY-MM-DD') : v;
          });
        }

        if (field.fieldType === 'Date' && effectiveDefaultValues && Array.isArray(effectiveDefaultValues)) {
          effectiveDefaultValues = effectiveDefaultValues.map((v) => {
            const date = dayjs.utc(v).local();
            return date.isValid() ? date.format('YYYY-MM-DD') : v;
          });
        }

        return {
          ...field,
          defaultValues,
          effectiveDefaultValues,
        };
      }),
    })),
  };
};

// API function to update template
const updateTemplateApi = async (template: Template, changeNote?: string): Promise<Template> => {
  const url = `${performanceReportingApiUrl()}/performance/templates/v1/templates/${template.id}`;
  const requestBody = {
    ...cleanTemplateForApi(template),
    ...(changeNote && { changeNote }),
  };

  const response = await fetcher({
    url,
    method: 'PUT',
    body: requestBody,
  });

  if (response.errorDetails) {
    throw response;
  }

  return transformApiResponseToFrontend(response);
};

export const useTemplateAutoSave = (
  templateId: number,
  onSuccessfulSave?: (updatedAt: string, savedTemplate?: Template) => void
) => {
  const queryClient = useQueryClient();
  const lastUserActivityRef = useRef<number>(Date.now());
  const focusedInputFieldRef = useRef<{ fieldId: string; fieldType: string } | null>(null);
  const latestTemplateRef = useRef<Template | null>(null);

  // Simple user activity tracking
  const trackUserActivity = useCallback(() => {
    lastUserActivityRef.current = Date.now();
  }, []);

  // Check if user is currently active (typing)
  const isUserActive = useCallback(() => {
    return Date.now() - lastUserActivityRef.current < 1500; // Active within last 1.5 seconds
  }, []);

  // Set input field focus state
  const setInputFieldFocus = useCallback((fieldId: string | null, fieldType?: string) => {
    if (fieldId && fieldType) {
      focusedInputFieldRef.current = { fieldId, fieldType };
    } else {
      focusedInputFieldRef.current = null;
    }
  }, []);

  // Auto-save mutation with true optimistic UI (no UI updates from server response)
  const autoSaveMutation = useMutation(
    ({ template, changeNote }: { template: Template; changeNote?: string }) => updateTemplateApi(template, changeNote),
    {
      onMutate: async ({ template }) => {
        // Cancel any outgoing refetches to prevent interference
        await queryClient.cancelQueries(['template', templateId]);

        // Snapshot the previous value for rollback on error
        const previousTemplate = queryClient.getQueryData<Template>(['template', templateId]);

        // DON'T update the cache here - let the local state be the source of truth
        // during editing. This prevents UI flickering and typing lag.

        // Return a context object with the snapshotted value
        return { previousTemplate };
      },
      onSuccess: (savedTemplate) => {
        // DON'T update the UI with server response during editing
        // The local state remains the source of truth for the UI
        // We only store the saved version in a separate cache key for later use
        queryClient.setQueryData(['template-saved', templateId], savedTemplate);

        // Update the backend template state with the full saved template
        if (onSuccessfulSave && savedTemplate.updatedAt) {
          onSuccessfulSave(savedTemplate.updatedAt, savedTemplate);
        }
      },
      onError: (err, { template }, context) => {
        console.error('Auto-save failed:', err);
        enqueueSnackbar('Failed to auto-save changes. Please review your changes and try again.', {
          variant: 'error',
        });

        // On error, we keep the local state unchanged
        // User can continue editing and try saving again
      },
    }
  );

  // Debounced auto-save function
  const debouncedAutoSave = useCallback(
    debounce((template: Template, changeNote?: string) => {
      // Always use the latest template state if available
      const templateToSave = latestTemplateRef.current || template;

      // Skip auto-save if user is actively typing
      if (isUserActive()) {
        // Reschedule autosave for when user becomes inactive with latest template
        setTimeout(() => debouncedAutoSave(templateToSave, changeNote), 1000);
        return;
      }

      // Skip auto-save if a date/time field is currently focused
      const focusedField = focusedInputFieldRef.current;
      if (focusedField && (focusedField.fieldType === 'Date' || focusedField.fieldType === 'Time')) {
        // Reschedule autosave with latest template
        setTimeout(() => debouncedAutoSave(templateToSave, changeNote), 1000);
        return;
      }

      if (!templateToSave.id) {
        return; // Don't auto-save new templates
      }

      // Skip if already saving to prevent race conditions
      if (autoSaveMutation.isLoading) {
        // Reschedule with latest template
        setTimeout(() => debouncedAutoSave(templateToSave, changeNote), 500);
        return;
      }

      autoSaveMutation.mutate({ template: templateToSave, changeNote });
    }, 800), // Reduced debounce time to 800ms for better responsiveness
    [autoSaveMutation, isUserActive]
  );

  // Trigger auto-save for property changes
  const triggerAutoSave = useCallback(
    (template: Template, changeNote?: string) => {
      // Store the latest template to ensure we always save the most recent state
      latestTemplateRef.current = template;

      // If user is actively typing, just store the template but don't trigger save yet
      if (isUserActive()) {
        return; // Let the existing debounce mechanism handle the save when user stops typing
      }

      // Cancel any pending auto-save to prevent race conditions
      debouncedAutoSave.cancel();

      // Use setTimeout to ensure the template state has been updated before triggering auto-save
      setTimeout(() => {
        // Always use the latest template state
        const templateToSave = latestTemplateRef.current || template;
        debouncedAutoSave(templateToSave, changeNote);
        latestTemplateRef.current = null;
      }, 0);
    },
    [debouncedAutoSave, isUserActive]
  );

  // Effect to handle auto-save when user becomes inactive
  useEffect(() => {
    const interval = setInterval(() => {
      // Check if user has been inactive and there's a template to save
      if (!isUserActive() && latestTemplateRef.current && !autoSaveMutation.isLoading) {
        const timeSinceLastActivity = Date.now() - lastUserActivityRef.current;

        // Auto-save if user has been inactive for more than 1 second
        if (timeSinceLastActivity >= 1000) {
          // DON'T call cleanTemplateForApi here - it will be called in updateTemplateApi
          autoSaveMutation.mutate({
            template: latestTemplateRef.current,
            changeNote: 'Auto-saved after typing',
          });

          // Clear the template ref to prevent repeated saves
          latestTemplateRef.current = null;
        }
      }
    }, 1000); // Check every second

    return () => clearInterval(interval);
  }, [autoSaveMutation, isUserActive]);

  // Cleanup function
  const cancelAutoSave = useCallback(() => {
    debouncedAutoSave.cancel();
  }, [debouncedAutoSave]);

  const enforceAutoSave = useCallback(
    async (template: Template, changeNote?: string): Promise<Template> => {
      // Cancel any pending debounced saves
      debouncedAutoSave.cancel();
      latestTemplateRef.current = null;

      if (autoSaveMutation.isLoading) {
        await new Promise<void>((resolve) => {
          const checkInterval = setInterval(() => {
            if (!autoSaveMutation.isLoading) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);
        });
      }
      return autoSaveMutation.mutateAsync({ template, changeNote });
    },
    [debouncedAutoSave, autoSaveMutation]
  );

  return {
    triggerAutoSave,
    trackUserActivity,
    setInputFieldFocus,
    isAutoSaving: autoSaveMutation.isLoading,
    autoSaveError: autoSaveMutation.error,
    // Cleanup function for debounced auto-save
    cancelAutoSave,
    // Enforce function for immediate save (pre-publish, etc.)
    enforceAutoSave,
  };
};
